import { ActivitySpinnerSize, ActivitySpinnerWidget, DialogType } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ToastType } from '~src/commonInfra/services/ToasterService';
import { ToasterService } from '~src/commonInfra/services/ToasterService';

import { GuidedOnboardingBannerCardData, GuidedOnboardingBannerService } from '../services/GuidedOnboardingBannerService';
import { GuidedOnboardingStyles } from '../styles/GuidedOnboardingStyles';
import GuidedOnboardingBannerCard from './GuidedOnboardingBannerCard';


interface GuidedOnboardingViewAllModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function LoadingSpinner() {
    return (
        <div className={GuidedOnboardingStyles.onboardingSpinnerContainer}>
            <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} data-tb-test-id="guided-onboarding-modal-spinner" />
        </div>
    );
}

function ModalHeader({ onClose }: { onClose: () => void }) {
    return (
        <div className={GuidedOnboardingStyles.modalHeader}>
            <h2 className={GuidedOnboardingStyles.modalTitle} data-tb-test-id="guided-onboarding-modal-title">
                {Messages.guidedOnboardingModalTitle()}
            </h2>
            <button
                className={GuidedOnboardingStyles.modalCloseButton}
                data-tb-test-id="guided-onboarding-modal-close-button"
                onClick={onClose}
                type="button"
                aria-label={Messages.guidedOnboardingModalCloseButton()}
            >
                ×
            </button>
        </div>
    );
}

function ModalContent({ cardData, onClose }: { cardData: GuidedOnboardingBannerCardData[], onClose: () => void }) {
    return (
        <div className={GuidedOnboardingStyles.modalContent}>
            <div className={GuidedOnboardingStyles.modalGrid}>
                {cardData.map(card => (
                    <div key={card.id}>
                        <GuidedOnboardingBannerCard {...card} onCardClick={onClose} data-tb-test-id="guided-onboarding-modal-card" />
                    </div>
                ))}
            </div>
        </div>
    );
}

export function GuidedOnboardingViewAllModal({ isOpen, onClose }: GuidedOnboardingViewAllModalProps): JSX.Element | null {
    const [cardData, setCardData] = React.useState<GuidedOnboardingBannerCardData[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            GuidedOnboardingBannerService.instance.getBannerCardData()
                .then(data => {
                    setCardData(data);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                    ToasterService.instance.pop({ type: ToastType.Error, text: Messages.guidedOnboardingServiceGetActionsError() });
                });
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className={GuidedOnboardingStyles.modalOverlay} data-tb-test-id="guided-onboarding-modal-overlay">
            <div className={GuidedOnboardingStyles.modalContainer} data-tb-test-id="guided-onboarding-view-all-modal">
                <ModalHeader onClose={onClose} />
                {isLoading ? (
                    <div className={GuidedOnboardingStyles.modalContent}>
                        <LoadingSpinner />
                    </div>
                ) : (
                    <ModalContent cardData={cardData} onClose={onClose} />
                )}
            </div>
        </div>
    );
}

export function openGuidedOnboardingViewAllModal(): Promise<any> {
    const modalInstance = ReactModalService.open({
        type: DialogType.Uber,
        title: Messages.guidedOnboardingModalTitle(),
        dialogContent: (
            <GuidedOnboardingViewAllModal
                isOpen={true}
                onClose={() => {
                    modalInstance.close();
                }}
            />
        ),
        dialogWidth: TabDialogWidth.width900px,
        showCloseButton: true
    });
    return modalInstance.result;
}
